<script setup lang="ts">
import { computed } from 'vue';
import { Button } from '@/components/ui/button';
import TrackCardList from '@/components/TrackCardList.vue';
import { usePlaylistUsageTracks } from '@/composables/api/useLiveTrackUsagesApi';
import type { TrackListExportSource } from '@/types/trackListExport';

/**
 * Drill-down from a row in the playlist usages inspector: the resolved tracks of one
 * bucket / custom playlist, rendered through the shared TrackCardList and paged from
 * the backend in batches of 500 (buckets can resolve thousands of tracks).
 */
const props = defineProps<{
  sourceType: 'bucket' | 'custom_playlist';
  sourceId: number;
  /** Owning "client / location — zone" — list names repeat across zones, so always show it. */
  ownershipLabel?: string;
  /**
   * Hide the type/name/count line — for hosts that already identify the list
   * (e.g. the Tracks tab inside the list's own inspector).
   */
  hideListHeader?: boolean;
}>();

const tracksQuery = usePlaylistUsageTracks(() => props.sourceType, () => props.sourceId);

const pages = computed(() => tracksQuery.data.value?.pages ?? []);
const tracks = computed(() => pages.value.flatMap((page) => page.data));
const listName = computed(() => pages.value[0]?.list.name ?? '');
const totalTracks = computed(() => pages.value[0]?.meta.total ?? tracks.value.length);
const sourceTypeLabel = computed(() =>
  props.sourceType === 'bucket' ? 'Bucket' : 'Custom List'
);

/**
 * Identifies this list to the export endpoint. One component covers both the
 * bucket and the custom-list surfaces, so both export buttons come from here.
 */
const exportSource = computed<TrackListExportSource>(() =>
  props.sourceType === 'bucket'
    ? { type: 'bucket', bucket_id: props.sourceId }
    : { type: 'custom_playlist', location_zone_custom_playlist_id: props.sourceId }
);

const exportLabel = computed(() =>
  `${sourceTypeLabel.value} - ${listName.value || props.sourceId}`
);
</script>

<template>
  <div class="flex flex-col gap-3 p-4" data-testid="playlist-usage-tracks-inspector">
    <div v-if="!hideListHeader" class="flex items-center gap-2 text-sm">
      <span class="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium">
        {{ sourceTypeLabel }}
      </span>
      <!-- The count used to sit here too; it now lives beside the export
           controls below, where every host of the track list shows it. -->
      <span class="min-w-0 truncate font-medium">{{ listName }}</span>
    </div>
    <div v-if="ownershipLabel" class="text-muted-foreground -mt-2 truncate text-xs">
      {{ ownershipLabel }}
    </div>

    <TrackCardList
      :tracks="tracks"
      :is-loading="tracksQuery.isLoading.value"
      :has-error="Boolean(tracksQuery.error.value)"
      empty-text="This list has no tracks right now."
      :export-source="exportSource"
      :export-label="exportLabel"
      :total-count="totalTracks"
    />

    <Button
      v-if="tracksQuery.hasNextPage.value"
      variant="outline"
      size="sm"
      class="self-center"
      :disabled="tracksQuery.isFetchingNextPage.value"
      data-testid="usage-tracks-load-more"
      @click="tracksQuery.fetchNextPage()"
    >
      {{ tracksQuery.isFetchingNextPage.value ? 'Loading…' : `Load more (${tracks.length} of ${totalTracks})` }}
    </Button>
  </div>
</template>
